/* When a harvest schedule fires, in the words the card uses: "Fri 9 Oct, 02:30".
 *
 * **Always in the schedule's own zone.** A reader agreeing "02:30 Asia/Kolkata" must see 02:30 on
 * the card whatever zone their own browser is in — rendering the instant in the reader's zone would
 * make a correctly stored schedule look as if it fires at the wrong time.
 */

import { WEEKDAY_ORDER, isLocalTime, type RecurrenceDraft } from './presentation';

interface LocalParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly minutes: number;
}

/** The wall-clock date and time an instant falls on in a zone, or `null` for a zone `Intl` does
 * not know. */
function localParts(instant: Date, timezone: string): LocalParts | null {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant);
    const read = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((part) => part.type === type)?.value ?? Number.NaN);
    const values = {
      year: read('year'),
      month: read('month'),
      day: read('day'),
      minutes: read('hour') * 60 + read('minute'),
    };
    return Object.values(values).some(Number.isNaN) ? null : values;
  } catch {
    return null;
  }
}

/** A calendar date plus a wall-clock time as the card prints it. Formatted in UTC because the date
 * is already local to the schedule's zone — formatting it in the reader's zone would shift it. */
function label(year: number, month: number, day: number, time: string): string {
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  const text = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
  return `${text.replace(',', '')}, ${time}`;
}

/** A stored occurrence as the card prints it, in the schedule's zone. `null` when the instant or
 * the zone cannot be read, so the caller can fall back to the raw value rather than to a dash. */
export function occurrenceLabel(
  iso: string | null | undefined,
  timezone: string | undefined,
): string | null {
  if (iso === null || iso === undefined || timezone === undefined) return null;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  const local = localParts(instant, timezone);
  if (local === null) return null;
  const hh = String(Math.floor(local.minutes / 60)).padStart(2, '0');
  const mm = String(local.minutes % 60).padStart(2, '0');
  return label(local.year, local.month, local.day, `${hh}:${mm}`);
}

/** When a draft would next fire, for the editor's preview — or `null` when the draft cannot fire
 * (an invalid time, a weekly cadence with no day chosen, an unknown zone).
 *
 * **A preview, not a promise.** The server computes the occurrence it stores, and a sweep claims it
 * at or shortly after that minute. This reasons in the zone's wall clock only, so a time skipped by
 * a daylight-saving change can preview a minute the server then moves; the stored value is what the
 * card shows once saved.
 */
export function nextOccurrence(draft: RecurrenceDraft, now: Date): string | null {
  if (!isLocalTime(draft.localTime)) return null;
  const days = draft.cadence === 'daily' ? WEEKDAY_ORDER : draft.weekdays;
  if (days.length === 0) return null;
  const today = localParts(now, draft.timezone);
  if (today === null) return null;

  const time = draft.localTime.trim();
  const [hh = 0, mm = 0] = time.split(':').map(Number);
  const target = hh * 60 + mm;
  for (let offset = 0; offset < 8; offset += 1) {
    if (offset === 0 && target <= today.minutes) continue;
    const date = new Date(Date.UTC(today.year, today.month - 1, today.day + offset, 12));
    /* Monday 0 through Sunday 6, the API's numbering; `getUTCDay` counts Sunday as 0. */
    const weekday = (date.getUTCDay() + 6) % 7;
    if (days.includes(weekday)) {
      return label(
        date.getUTCFullYear(),
        date.getUTCMonth() + 1,
        date.getUTCDate(),
        time,
      );
    }
  }
  return null;
}

export type Age =
  | { readonly kind: 'justNow' }
  | { readonly kind: 'minutes' | 'hours' | 'days'; readonly count: number };

/** How long ago an instant was, in the largest whole unit. A future instant reads as just now:
 * clock skew between this platform and a browser is real, and "in -1 hours" is a rendering bug. */
export function ageOf(iso: string, now: Date): Age | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes < 1) return { kind: 'justNow' };
  if (minutes < 60) return { kind: 'minutes', count: minutes };
  if (minutes < 60 * 24) return { kind: 'hours', count: Math.floor(minutes / 60) };
  return { kind: 'days', count: Math.floor(minutes / (60 * 24)) };
}
